/* ============================================
   useCachedQuery — React Query wrapper for useApi
   Provides stale-while-revalidate caching for all CRUD tables.
   Data persists across route changes (no re-fetch on tab switch).
   ============================================ */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from './useApi'

type CrudTable = 'empresa' | 'servicios' | 'clientes' | 'cotizaciones' | 'perfiles_equipo' | 'facturas' | 'config_detraccion' | 'tipo_cambio' | 'proyectos' | 'tareas' | 'tarea_evidencias' | 'config_tarifa_cliente' | 'portal_accesos' | 'factura_comprobantes' | 'contactos' | 'contacto_empresa' | 'movimientos_caja' | 'pagos_programados' | 'pagos_recurrentes' | 'apps_catalogo' | 'expedientes_sunat'

// Route mapping for tables with non-standard API paths (shared with supabase-compat)
const TABLE_ROUTES: Record<string, string> = {
  'conversaciones': '/api/chat/conversaciones',
  'mensajes': '/api/chat/mensajes',
  'mensajes_programados': '/api/chat/mensajes-programados',
  'perfiles_equipo': '/api/config/perfiles-equipo',
  'config_detraccion': '/api/config/detraccion',
  'config_tarifa_cliente': '/api/config/tarifa-cliente',
  'portal_accesos': '/api/config/portal-accesos',
  'centrales_costo': '/api/contabilidad/centrales-costo',
  'costos_estimados': '/api/contabilidad/costos-estimados',
  'asignaciones_central': '/api/contabilidad/asignaciones-central',
  'movimientos_caja': '/api/contabilidad/movimientos',
  'pagos_programados': '/api/contabilidad/pagos-programados',
  'expedientes_sunat': '/api/expedientes',
  'pagos_recurrentes': '/api/contabilidad/pagos-recurrentes',
  'gastos_proyecto': '/api/contabilidad/gastos-proyecto',
  'push_subscriptions': '/api/config/push-subscriptions',
  'apps_catalogo': '/api/apps',
}

function getApiPath(table: string): string {
  return TABLE_ROUTES[table] || `/api/${table}`
}

/**
 * Cached version of useApi.get().
 * Data stays in cache for 5 minutes (staleTime) and survives route changes.
 *
 * Usage:
 *   const { data: clientes, isLoading } = useCachedQuery<Cliente>('clientes')
 */
export function useCachedQuery<T>(table: CrudTable) {
  const api = useApi()

  return useQuery<T[]>({
    queryKey: [table],
    queryFn: () => api.get<T[]>(getApiPath(table)),
    staleTime: 5 * 60 * 1000,     // 5 min — no refetch if fresh
    gcTime: 10 * 60 * 1000,       // 10 min — keep in memory
    refetchOnWindowFocus: false,   // Don't refetch when user alt-tabs
  })
}

/**
 * Mutation helpers that auto-invalidate the cache after CUD operations.
 *
 * Usage:
 *   const { addMut, updateMut, removeMut } = useCachedMutations('clientes')
 *   addMut.mutateAsync(record)
 */
export function useCachedMutations(table: CrudTable) {
  const api = useApi()
  const qc = useQueryClient()

  const invalidate = () => qc.invalidateQueries({ queryKey: [table] })

  const addMut = useMutation({
    mutationFn: (record: Record<string, unknown>) => api.post(getApiPath(table), record),
    onSuccess: invalidate,
  })

  const updateMut = useMutation({
    mutationFn: (record: Record<string, unknown>) => api.put(`${getApiPath(table)}/${record.id}`, record),
    onSuccess: invalidate,
  })

  const removeMut = useMutation({
    mutationFn: (id: number) => api.del(`${getApiPath(table)}/${id}`),
    onSuccess: invalidate,
  })

  return { addMut, updateMut, removeMut, invalidate }
}
